import { useEffect, useState } from 'react';
import { Video, Check, CalendarClock, Pencil, Ban, Users, ClipboardList } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { supabase } from '../services/supabase';
import { useDeepLinkHighlight } from '../lib/useDeepLinkHighlight';
import { leerVarias, listaEnCastellano } from '../lib/leerVarias';
import { OpcionesDeEntrenador } from '../components/OpcionesDeEntrenador';
import { ROLES_ASIGNABLES, type EntrenadorAsignable } from '../lib/entrenadoresAsignables';

// Los nombres de lo que se lee, para poder contar CUÁL no se pudo leer.
const QUE_POOL = 'el pool de monitores';
const QUE_NOMBRES = 'los nombres de los entrenadores';
const QUE_CREDITOS = 'los créditos de vídeo';

type Status = 'pending' | 'scheduled' | 'completed' | 'cancelled';
type Tab = 'bookings' | 'assignments';

interface Booking {
  id: string;
  user_id: string;
  trainer_id: string | null;
  status: Status;
  scheduled_at: string | null;
  completed_at: string | null;
  notes: string | null;
  created_at: string;
  student: { full_name: string } | null;
  trainer: { full_name: string } | null;
}


interface AssignmentRow {
  user_id: string;
  full_name: string;
  assigned_video_trainer_id: string | null;
  trainer_name: string | null;
  credits_remaining: number;
}

const STATUS_LABEL: Record<Status, string> = {
  pending: 'Solicitado',
  scheduled: 'Agendado',
  completed: 'Realizado',
  cancelled: 'Cancelado',
};
const STATUS_BADGE: Record<Status, string> = {
  pending: 'badge-gold',
  scheduled: 'badge-success',
  completed: 'badge-success',
  cancelled: 'badge-error',
};

const FILTERS: Array<{ key: 'activos' | Status; label: string }> = [
  { key: 'activos', label: 'Activos' },
  { key: 'pending', label: 'Solicitados' },
  { key: 'scheduled', label: 'Agendados' },
  { key: 'completed', label: 'Realizados' },
  { key: 'cancelled', label: 'Cancelados' },
];

function fmt(ts: string | null): string {
  if (!ts) return '—';
  return new Date(ts).toLocaleString('es-ES', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default function VideoAnalysisPage() {
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState<Tab>('bookings');
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'activos' | Status>('activos');
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState('');

  // Asignaciones (paridad con la pestaña Asignaciones de GymPage). A diferencia
  // de GYM —que usa la tabla join gym_monitor_assignments— vídeo análisis usa la
  // columna profiles.assigned_video_trainer_id (divergencia histórica de J2,
  // documentada en ADR-0030). La asignación es manual: el webhook NO auto-asigna
  // (Camino A), solo avisa al admin por compra.
  const [pool, setPool] = useState<EntrenadorAsignable[]>([]);
  const [rows, setRows] = useState<AssignmentRow[]>([]);
  const [assLoading, setAssLoading] = useState(true);
  // «Sin reservas en esta vista» es una afirmación sobre las reservas y manda a
  // mirar el filtro, que es donde no está el problema.
  const [errorBookings, setErrorBookings] = useState<string | null>(null);
  // La pestaña de asignaciones tiene DOS afirmaciones peligrosas y una de
  // ellas es una instrucción: «Activa el permiso en Monitores antes de
  // asignar». Si lo que falló es la lectura del pool, el admin se va a
  // Monitores a activar un permiso que ya está puesto. Por eso el fallo del
  // pool se separa de los demás.
  const [errorPool, setErrorPool] = useState<string | null>(null);
  const [errorAlumnos, setErrorAlumnos] = useState<string | null>(null);
  const [fallidosAsig, setFallidosAsig] = useState<string[]>([]);

  useEffect(() => {
    loadBookings();
    loadAssignments();
  }, []);

  async function loadBookings() {
    setLoading(true);
    const { data, error: errorLectura } = await supabase
      .from('video_analysis_bookings')
      .select(
        'id, user_id, trainer_id, status, scheduled_at, completed_at, notes, created_at, student:profiles!video_analysis_bookings_user_id_fkey(full_name), trainer:profiles!video_analysis_bookings_trainer_id_fkey(full_name)'
      )
      .order('created_at', { ascending: false });
    // Dos embeds a `profiles` por FK distinta (alumno y analista): es la forma
    // del #496, donde el 300 PGRST201 descartado se pintaba como «sin pedidos».
    if (errorLectura) {
      setErrorBookings(errorLectura.message || 'Error desconocido');
      setBookings([]);
      setLoading(false);
      return;
    }
    setErrorBookings(null);
    setBookings((data ?? []) as unknown as Booking[]);
    setLoading(false);
  }

  async function loadAssignments() {
    setAssLoading(true);
    // pool = monitores elegibles (dropdown); monitorsAll = para resolver el
    // nombre del entrenador asignado aunque ya no esté en el pool (perdió el flag).
    const noLeidos: string[] = [];
    const { datos, fallidos } = await leerVarias([
      {
        que: QUE_POOL,
        // ⚠️ DIRECCIÓN TAMBIÉN PUEDE ANALIZAR VÍDEO. Se ensancha el ROL, no el
        // requisito: `can_do_video_analysis` sigue mandando, y hoy ningún
        // director lo tiene puesto (medido). Entra para que el día que se lo
        // pongas funcione.
        consulta: supabase
          .from('profiles')
          .select('id, full_name, role')
          .in('role', ROLES_ASIGNABLES)
          .eq('can_do_video_analysis', true)
          .eq('active', true)
          .order('full_name'),
      },
      {
        que: QUE_NOMBRES,
        // ⚠️ ESTA TAMBIÉN, Y NO ES LA MISMA COSA. Resuelve el NOMBRE del
        // analista ya asignado, incluso si perdió el flag y salió del pool. Si
        // se ensancha solo el pool, el día que asignes a alguien de dirección el
        // desplegable lo ofrece y la tabla enseña un hueco donde iba su nombre:
        // el arreglo se vería a medias y parecería un fallo de datos.
        consulta: supabase
          .from('profiles')
          .select('id, full_name, role')
          .in('role', ROLES_ASIGNABLES),
      },
      {
        que: QUE_CREDITOS,
        consulta: supabase
          .from('user_service_credits')
          .select('user_id, count_remaining')
          .eq('service_type', 'video_analysis'),
      },
    ]);
    const [poolData, monitorsAll, creditRows] = datos;

    setErrorPool(fallidos.includes(QUE_POOL) ? 'No se ha podido leer el pool de monitores.' : null);
    if (fallidos.includes(QUE_NOMBRES)) noLeidos.push(QUE_NOMBRES);

    const poolList = (poolData ?? []) as EntrenadorAsignable[];
    setPool(poolList);

    const monitorName: Record<string, string> = {};
    ((monitorsAll ?? []) as EntrenadorAsignable[]).forEach((m) => {
      monitorName[m.id] = m.full_name;
    });

    // Saldo de créditos de vídeo por alumno (suma de count_remaining).
    const creditsByUser: Record<string, number> = {};
    ((creditRows ?? []) as Array<{ user_id: string; count_remaining: number }>).forEach((c) => {
      creditsByUser[c.user_id] = (creditsByUser[c.user_id] ?? 0) + (c.count_remaining ?? 0);
    });

    // Relevantes = alumnos que han comprado vídeo análisis (tienen crédito) o ya
    // tienen un entrenador asignado. Espeja el scope de GymPage (por créditos).
    const { data: alumnosData, error: errorLectura } = await supabase
      .from('profiles')
      .select('id, full_name, assigned_video_trainer_id')
      .eq('role', 'alumno')
      .order('full_name');

    // Los créditos NO son adorno: el `.filter` de abajo decide QUIÉN sale de
    // la lista con ellos. Sin créditos solo quedan los que ya tienen
    // entrenador asignado, o sea que justo los que hay que asignar
    // desaparecen, y la tabla dice «Sin alumnos con vídeo análisis comprado».
    if (errorLectura || fallidos.includes(QUE_CREDITOS)) {
      setErrorAlumnos(
        errorLectura
          ? 'No se ha podido leer la lista de alumnos.'
          : 'No se han podido leer los créditos de vídeo, y son los que deciden quién sale en esta lista.'
      );
      setRows([]);
      setFallidosAsig(noLeidos);
      setAssLoading(false);
      return;
    }
    setErrorAlumnos(null);

    const alumnos = (alumnosData ?? []) as Array<{
      id: string;
      full_name: string;
      assigned_video_trainer_id: string | null;
    }>;

    const merged: AssignmentRow[] = alumnos
      .filter((a) => a.id in creditsByUser || a.assigned_video_trainer_id)
      .map((a) => ({
        user_id: a.id,
        full_name: a.full_name,
        assigned_video_trainer_id: a.assigned_video_trainer_id,
        trainer_name: a.assigned_video_trainer_id
          ? (monitorName[a.assigned_video_trainer_id] ?? '—')
          : null,
        credits_remaining: creditsByUser[a.id] ?? 0,
      }));

    setRows(merged);
    setFallidosAsig(noLeidos);
    setAssLoading(false);
  }

  async function assignTrainer(userId: string, trainerId: string) {
    const value = trainerId || null;
    setBusy(userId);
    setError('');
    const { error: e } = await supabase
      .from('profiles')
      .update({ assigned_video_trainer_id: value })
      .eq('id', userId);
    if (e) setError(e.message);
    await loadAssignments();
    setBusy(null);
  }

  // Asignar (o reasignar) el ANALISTA de un booking concreto. La columna
  // trainer_id y la RLS is_admin() ya lo permiten. Tras asignarlo, el booking
  // deja de estar "sin analista" y pasa a "pendiente de agendar" del entrenador
  // (que ya puede agendarlo: RLS UPDATE trainer_id = auth.uid()).
  async function assignBookingTrainer(b: Booking, trainerId: string) {
    const value = trainerId || null;
    setBusy(b.id);
    setError('');
    const { error: e } = await supabase
      .from('video_analysis_bookings')
      .update({ trainer_id: value })
      .eq('id', b.id);
    if (e) setError(e.message);
    await loadBookings();
    setBusy(null);
  }

  async function schedule(b: Booking) {
    const when = window.prompt(
      'Fecha y hora de la sesión (YYYY-MM-DD HH:MM):',
      new Date().toISOString().slice(0, 16).replace('T', ' ')
    );
    if (!when) return;
    const iso = new Date(when.replace(' ', 'T')).toISOString();
    if (Number.isNaN(Date.parse(iso))) {
      setError('Fecha no válida.');
      return;
    }
    setBusy(b.id);
    setError('');
    const { error: e } = await supabase
      .from('video_analysis_bookings')
      .update({ status: 'scheduled', scheduled_at: iso })
      .eq('id', b.id);
    if (e) setError(e.message);
    await loadBookings();
    setBusy(null);
  }

  // D2 — Editar la fecha/hora de una solicitud ya agendada (mantiene 'scheduled').
  async function editSchedule(b: Booking) {
    const current = (b.scheduled_at ? new Date(b.scheduled_at) : new Date())
      .toISOString()
      .slice(0, 16)
      .replace('T', ' ');
    const when = window.prompt('Nueva fecha y hora de la sesión (YYYY-MM-DD HH:MM):', current);
    if (!when) return;
    const iso = new Date(when.replace(' ', 'T')).toISOString();
    if (Number.isNaN(Date.parse(iso))) {
      setError('Fecha no válida.');
      return;
    }
    setBusy(b.id);
    setError('');
    const { error: e } = await supabase
      .from('video_analysis_bookings')
      .update({ status: 'scheduled', scheduled_at: iso })
      .eq('id', b.id);
    if (e) setError(e.message);
    await loadBookings();
    setBusy(null);
  }

  // D2 — Cancelar una solicitud (pending/scheduled). El crédito NO se gastó (solo
  // se consume al completar), así que queda disponible: no hay reembolso.
  async function cancelBooking(b: Booking) {
    const ok = window.confirm(
      `¿Cancelar la solicitud de ${b.student?.full_name ?? 'este alumno'}? El crédito queda disponible para una nueva solicitud.`
    );
    if (!ok) return;
    setBusy(b.id);
    setError('');
    const { error: e } = await supabase
      .from('video_analysis_bookings')
      .update({ status: 'cancelled' })
      .eq('id', b.id);
    if (e) setError(e.message);
    await loadBookings();
    setBusy(null);
  }

  async function complete(b: Booking) {
    setBusy(b.id);
    setError('');
    const { data, error: e } = await supabase.rpc('complete_video_analysis', {
      p_booking_id: b.id,
    });
    const res = data as { ok?: boolean; error?: string } | null;
    if (e) setError(e.message);
    else if (res && res.ok === false) setError(res.error ?? 'No se pudo completar');
    await loadBookings();
    setBusy(null);
  }

  const visible = bookings.filter((b) =>
    filter === 'activos' ? b.status === 'pending' || b.status === 'scheduled' : b.status === filter
  );

  // C-3.1: al llegar desde la agenda con ?booking=<id>, situar la pestaña Reservas
  // y ajustar el filtro para que esa reserva esté en la vista (los 'scheduled' ya
  // entran en "Activos", pero cubrimos cualquier estado por robustez).
  const bookingParam = searchParams.get('booking');
  useEffect(() => {
    if (!bookingParam || loading) return;
    const b = bookings.find((x) => x.id === bookingParam);
    if (!b) return;
    setTab('bookings');
    const inActivos = b.status === 'pending' || b.status === 'scheduled';
    setFilter((f) => {
      const shown = f === 'activos' ? inActivos : b.status === f;
      return shown ? f : inActivos ? 'activos' : b.status;
    });
  }, [bookingParam, loading, bookings]);

  // Localiza la fila `reserva-row-<id>`, hace scroll y la resalta.
  const highlighted = useDeepLinkHighlight(
    bookingParam,
    'reserva-row-',
    !loading && tab === 'bookings',
    visible.length
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2">
        <Video size={20} className="text-gold-primary" />
        <h1 className="font-oswald text-xl text-white">Análisis de vídeo</h1>
      </div>

      {error && <p className="text-error text-sm bg-error/10 rounded-lg p-3">{error}</p>}

      {/* Tabs */}
      <div className="flex gap-3">
        <button
          onClick={() => setTab('bookings')}
          data-testid="tab-reservas"
          className={tab === 'bookings' ? 'btn-gold' : 'btn-ghost'}
        >
          <ClipboardList size={15} className="inline mr-2" />
          Reservas
        </button>
        <button
          onClick={() => setTab('assignments')}
          data-testid="tab-asignaciones"
          className={tab === 'assignments' ? 'btn-gold' : 'btn-ghost'}
        >
          <Users size={15} className="inline mr-2" />
          Asignaciones
        </button>
      </div>

      {/* ---- RESERVAS ---- */}
      {tab === 'bookings' && (
        <>
          <div className="flex gap-1 bg-bg-elevated p-1 rounded-lg w-fit">
            {FILTERS.map((f) => (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                className={`px-4 py-1.5 rounded text-sm font-inter transition-colors ${filter === f.key ? 'bg-gold-primary text-bg-primary' : 'text-text-secondary hover:text-white'}`}
              >
                {f.label}
              </button>
            ))}
          </div>

          <div className="card p-0 overflow-hidden">
            {loading ? (
              <div className="flex items-center justify-center h-48">
                <div className="w-8 h-8 border-2 border-gold-primary/30 border-t-gold-primary rounded-full animate-spin" />
              </div>
            ) : (
              <table className="w-full">
                <thead className="border-b border-bg-border bg-bg-elevated">
                  <tr>
                    <th className="table-th">Alumno</th>
                    <th className="table-th">Entrenador</th>
                    <th className="table-th">Estado</th>
                    <th className="table-th">Agendada</th>
                    <th className="table-th">Realizada</th>
                    <th className="table-th">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {errorBookings ? (
                    <tr>
                      <td colSpan={6} className="table-td text-center py-12">
                        <span className="text-error text-sm" data-testid="bookings-ilegibles">
                          No se han podido leer las reservas. No toque el filtro: la consulta ha
                          fallado.
                        </span>
                        <button
                          className="btn-secondary block mx-auto mt-3"
                          onClick={loadBookings}
                          data-testid="bookings-reintentar"
                        >
                          Reintentar
                        </button>
                      </td>
                    </tr>
                  ) : visible.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="table-td text-center text-text-secondary py-12">
                        Sin reservas en esta vista.
                      </td>
                    </tr>
                  ) : (
                    visible.map((b) => (
                      <tr
                        key={b.id}
                        data-testid={`reserva-row-${b.id}`}
                        className={`table-row transition-shadow ${
                          highlighted === b.id
                            ? 'ring-2 ring-inset ring-gold-primary bg-gold-primary/5'
                            : ''
                        }`}
                      >
                        <td className="table-td font-medium">{b.student?.full_name ?? '—'}</td>
                        <td className="table-td">
                          {b.status === 'pending' || b.status === 'scheduled' ? (
                            <select
                              className={`input h-8 text-xs py-0 max-w-[12rem] ${b.trainer_id ? '' : 'border-error/60'}`}
                              value={b.trainer_id ?? ''}
                              disabled={busy === b.id}
                              onChange={(e) => assignBookingTrainer(b, e.target.value)}
                              data-testid={`asignar-analista-${b.id}`}
                              title="Analista asignado a este vídeo-análisis"
                            >
                              <option value="">— Sin analista —</option>
                              <OpcionesDeEntrenador lista={pool} />
                            </select>
                          ) : (
                            <span className="text-text-secondary">
                              {b.trainer?.full_name ?? '—'}
                            </span>
                          )}
                        </td>
                        <td className="table-td">
                          <span className={STATUS_BADGE[b.status]}>{STATUS_LABEL[b.status]}</span>
                        </td>
                        <td className="table-td text-text-secondary">{fmt(b.scheduled_at)}</td>
                        <td className="table-td text-text-secondary">{fmt(b.completed_at)}</td>
                        <td className="table-td">
                          {(b.status === 'pending' || b.status === 'scheduled') && (
                            <div className="flex items-center gap-2">
                              {b.status === 'pending' ? (
                                <button
                                  onClick={() => schedule(b)}
                                  disabled={busy === b.id}
                                  data-testid={`agendar-${b.id}`}
                                  title="Agendar"
                                  className="text-text-secondary hover:text-gold-primary transition-colors"
                                >
                                  <CalendarClock size={16} />
                                </button>
                              ) : (
                                <button
                                  onClick={() => editSchedule(b)}
                                  disabled={busy === b.id}
                                  data-testid={`editar-${b.id}`}
                                  title="Editar fecha/hora"
                                  className="text-text-secondary hover:text-gold-primary transition-colors"
                                >
                                  <Pencil size={16} />
                                </button>
                              )}
                              <button
                                onClick={() => complete(b)}
                                disabled={busy === b.id}
                                title="Marcar realizada (decrementa crédito)"
                                className="text-text-secondary hover:text-success transition-colors"
                              >
                                <Check size={16} />
                              </button>
                              <button
                                onClick={() => cancelBooking(b)}
                                disabled={busy === b.id}
                                data-testid={`cancelar-${b.id}`}
                                title="Cancelar solicitud"
                                className="text-text-secondary hover:text-error transition-colors"
                              >
                                <Ban size={16} />
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}

      {/* ---- ASIGNACIONES ---- */}
      {tab === 'assignments' && (
        <>
          {fallidosAsig.length > 0 && (
            <p
              className="text-error text-sm border border-error/30 rounded px-3 py-2"
              data-testid="asignaciones-fallidos"
            >
              No se ha podido leer {listaEnCastellano(fallidosAsig)}. Lo que salga «—» en
              «Entrenador asignado» está sin resolver, no sin asignar.
            </p>
          )}

          {/* Pool de monitores elegibles (referencia). El flag can_do_video_analysis
              se gestiona en MonitoresPage, igual que is_physical_trainer en GYM. */}
          <div className="card p-4">
            <p className="text-text-secondary text-xs font-inter mb-2 flex items-center gap-2">
              <Users size={14} /> Monitores con análisis de vídeo habilitado
            </p>
            {errorPool ? (
              <p className="text-error text-sm" data-testid="pool-ilegible">
                {errorPool} No vaya a activar el permiso en Monitores: puede que ya esté puesto y lo
                que ha fallado sea la consulta.
              </p>
            ) : pool.length === 0 ? (
              <p className="text-error text-sm" data-testid="pool-vacio">
                Nadie tiene habilitado el análisis de vídeo. Activa el permiso en Monitores antes
                de asignar — puede ser un entrenador o alguien de dirección.
              </p>
            ) : (
              <div className="flex flex-wrap gap-2" data-testid="pool-monitores">
                {pool.map((m) => (
                  <span key={m.id} className="badge-gold text-xs">
                    {m.full_name}
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className="card p-0 overflow-hidden">
            {assLoading ? (
              <div className="flex items-center justify-center h-48">
                <div className="w-8 h-8 border-2 border-gold-primary/30 border-t-gold-primary rounded-full animate-spin" />
              </div>
            ) : (
              <table className="w-full">
                <thead className="border-b border-bg-border bg-bg-elevated">
                  <tr>
                    <th className="table-th">Alumno</th>
                    <th className="table-th text-center">Créditos vídeo</th>
                    <th className="table-th">Entrenador asignado</th>
                    <th className="table-th">Asignar / reasignar</th>
                  </tr>
                </thead>
                <tbody>
                  {errorAlumnos ? (
                    <tr>
                      <td colSpan={4} className="table-td text-center py-12">
                        <span className="text-error text-sm" data-testid="asignaciones-ilegibles">
                          {errorAlumnos}
                        </span>
                        <button
                          className="btn-secondary block mx-auto mt-3"
                          onClick={loadAssignments}
                          data-testid="asignaciones-reintentar"
                        >
                          Reintentar
                        </button>
                      </td>
                    </tr>
                  ) : rows.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="table-td text-center text-text-secondary py-12">
                        Sin alumnos con vídeo análisis comprado ni entrenador asignado.
                      </td>
                    </tr>
                  ) : (
                    rows.map((r) => (
                      <tr
                        key={r.user_id}
                        className="table-row"
                        data-testid={`asignacion-row-${r.user_id}`}
                      >
                        <td className="table-td font-medium">{r.full_name}</td>
                        <td className="table-td text-center">
                          <span
                            className={`font-oswald text-lg ${r.credits_remaining === 0 ? 'text-text-secondary' : 'text-gold-primary'}`}
                          >
                            {r.credits_remaining}
                          </span>
                        </td>
                        <td className="table-td">
                          {r.assigned_video_trainer_id ? (
                            <span className="text-success text-xs font-medium">
                              {r.trainer_name}
                            </span>
                          ) : (
                            <span className="text-error text-xs">Sin asignar</span>
                          )}
                        </td>
                        <td className="table-td">
                          <select
                            className="input h-8 text-xs py-0 max-w-[12rem]"
                            value={r.assigned_video_trainer_id ?? ''}
                            disabled={busy === r.user_id}
                            onChange={(e) => assignTrainer(r.user_id, e.target.value)}
                            data-testid={`asignar-select-${r.user_id}`}
                            title="Entrenador de análisis de vídeo asignado"
                          >
                            <option value="">— Sin asignar —</option>
                            <OpcionesDeEntrenador lista={pool} />
                          </select>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </div>
  );
}
